// Import 3rd-party dependencies
import { Directive, Input, ElementRef, Renderer2, OnInit, OnDestroy } from '@angular/core';
import { from, fromEvent } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { SubSink } from 'subsink';

// Import project-specific dependencies
import { TOOLTIP_DIRECTIVE_CONSTS } from '@app/config/constants';

enum TooltipPosition {
  TOP = 'top',
  BOTTOM = 'bottom',
  LEFT = 'left',
  RIGHT = 'right'
}

@Directive({
  selector: '[appTooltip]'
})
export class TooltipDirective implements OnInit, OnDestroy {
  private subs: SubSink;
  @Input('appTooltip') tooltipText: string;
  @Input() tooltipPosition: string;
  @Input() tooltipCustomClass: string;
  tooltip: HTMLElement;
  consts = TOOLTIP_DIRECTIVE_CONSTS;

  constructor(private elementRef: ElementRef, private renderer: Renderer2) {}

  ngOnInit(): void {
    this.subs = new SubSink();

    // show tooltip on mouseenter/mousedown/click
    this.subs.sink = from(this.consts.EVENTS.SHOW)
      .pipe(mergeMap((event) => fromEvent(this.elementRef.nativeElement, event)))
      .subscribe(() => this.showTooltip());

    // hide tooltip on mouseleave/mousedown/mouse wheel scroll
    this.subs.sink = from(this.consts.EVENTS.HIDE)
      .pipe(mergeMap((event) => fromEvent(this.elementRef.nativeElement, event)))
      .subscribe(() => this.hideTooltip());
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // append tooltip
  appendTooltip(): void {
    this.tooltip = this.renderer.createElement('span') as HTMLElement;
    this.renderer.appendChild(this.tooltip, this.renderer.createText(this.tooltipText));
    this.renderer.appendChild(document.body, this.tooltip);
    this.renderer.addClass(this.tooltip, 'tooltip');
    this.renderer.addClass(this.tooltip, this.tooltipCustomClass != undefined ? this.tooltipCustomClass : 'none');

    // change tooltip left/right position when screen resize
    const element = this.elementRef.nativeElement as HTMLElement;
    const elementPosition = element.getBoundingClientRect();
    if (
      this.tooltipPosition.trim() === TooltipPosition.LEFT ||
      this.tooltipPosition.trim() === TooltipPosition.RIGHT
    ) {
      if (elementPosition.x < this.consts.MIN_SCREEN_WIDTH || window.innerWidth > this.consts.MAX_SCREEN_WIDTH) {
        this.tooltipPosition = TooltipPosition.RIGHT;
      } else {
        this.tooltipPosition = TooltipPosition.LEFT;
      }
    }
    this.renderer.addClass(this.tooltip, `tooltip-${this.tooltipPosition}`);
  }

  // set position for tooltip
  setTooltipPosition(): void {
    const hostPos = (this.elementRef.nativeElement as HTMLElement).getBoundingClientRect();
    const tooltipPos = this.tooltip.getBoundingClientRect();
    const scrollPos = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    let top: number;
    let left: number;

    switch (this.tooltipPosition) {
      case TooltipPosition.TOP:
        top = hostPos.top - tooltipPos.height;
        left = hostPos.left + (hostPos.width - tooltipPos.width) / 2;
        break;
      case TooltipPosition.BOTTOM:
        top = hostPos.bottom + this.consts.POSITION_OFFSET;
        left = hostPos.left + (hostPos.width - tooltipPos.width) / 2;
        break;
      case TooltipPosition.LEFT:
        top = hostPos.top + (hostPos.height - tooltipPos.height) / 2;
        left = hostPos.left - tooltipPos.width - this.consts.POSITION_OFFSET;
        break;
      default:
        // show tooltip in right position
        top = hostPos.top + (hostPos.height - tooltipPos.height) / 2;
        left = hostPos.right + this.consts.POSITION_OFFSET;
        break;
    }

    this.renderer.setStyle(this.tooltip, 'top', `${top + scrollPos}px`);
    this.renderer.setStyle(this.tooltip, 'left', `${left}px`);
  }

  // show tooltip
  showTooltip(): void {
    if (!this.tooltip) {
      this.appendTooltip();
      this.setTooltipPosition();
      this.renderer.addClass(this.tooltip, 'tooltip-show');
    }
  }

  // hide tooltip
  hideTooltip(): void {
    if (this.tooltip) {
      this.renderer.removeClass(this.tooltip, 'tooltip-show');
      this.renderer.removeChild(document.body, this.tooltip);
      this.tooltip = null;
    }
  }
}
